import { useState } from "react";
import type { ReactElement } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import type {
  CatalogListResponse,
  DemoUsageResponse,
  InviteListResponse,
  InviteResponse,
  SessionListResponse,
} from "../api/types";
import { InviteCatalogPicker } from "../components/InviteCatalogPicker";
import { useAuth } from "../contexts/AuthContext";

/**
 * Cycle 18 STORY-1801 — Unified admin nav across all admin sections.
 *
 * Admin sections:
 * - /admin            → Invites + Analytics (this page, default)
 * - /admin/leads      → Lead management (Cycle 11+17)
 * - /admin/calls      → Click-to-call (Cycle 14+17)
 * - /admin/health     → Portfolio health dashboard (Cycle 15)
 *
 * Old: only Invites/Analytics tabs visible → user thought "leads/calls disappeared".
 * New: top-level nav with all 4 sections, active highlighted.
 */
function AdminNav(): ReactElement {
  const location = useLocation();
  const sections = [
    { path: "/admin", label: "📨 Invites + Analytics", match: (p: string) => p === "/admin" || (p.startsWith("/admin") && !["/admin/leads", "/admin/calls", "/admin/health", "/admin/tenants", "/admin/audit-log"].some((x) => p.startsWith(x))) },
    { path: "/admin/leads", label: "🎯 Leads", match: (p: string) => p.startsWith("/admin/leads") },
    { path: "/admin/calls", label: "📞 Calls", match: (p: string) => p.startsWith("/admin/calls") },
    { path: "/admin/tenants", label: "🏢 Tenants", match: (p: string) => p.startsWith("/admin/tenants") },
    { path: "/admin/audit-log", label: "📋 Audit Log", match: (p: string) => p.startsWith("/admin/audit-log") },
    { path: "/admin/health", label: "💚 Health Dashboard", match: (p: string) => p.startsWith("/admin/health") },
  ];

  return (
    <nav style={{
      display: "flex",
      gap: 4,
      padding: "0 24px",
      marginBottom: 16,
      borderBottom: "1px solid #e1e6f0",
      flexWrap: "wrap",
    }}>
      {sections.map((s) => {
        const active = s.match(location.pathname);
        return (
          <Link
            key={s.path}
            to={s.path}
            style={{
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              color: active ? "#0b5fff" : "#5a6378",
              textDecoration: "none",
              borderBottom: active ? "2px solid #0b5fff" : "2px solid transparent",
              marginBottom: -1,
              transition: "all 0.15s",
            }}
          >
            {s.label}
          </Link>
        );
      })}
    </nav>
  );
}

// Exported for reuse in AdminLeadsPage / AdminCallsPage / HealthDashboardPage
export { AdminNav };

export function AdminPage(): ReactElement {
  const [tab, setTab] = useState<"invites" | "analytics">("invites");
  const { hasAdminJwt } = useAuth();

  if (!hasAdminJwt) {
    return <AdminLoginPrompt />;
  }

  return (
    <section className="admin">
      <header className="admin__header">
        <h1>Admin Panel</h1>
        <AdminLogoutButton />
      </header>
      <AdminNav />
      <nav className="admin__tabs">
        <button
          className={tab === "invites" ? "admin__tab admin__tab--active" : "admin__tab"}
          onClick={() => setTab("invites")}
        >
          Invites
        </button>
        <button
          className={tab === "analytics" ? "admin__tab admin__tab--active" : "admin__tab"}
          onClick={() => setTab("analytics")}
        >
          Analytics
        </button>
      </nav>
      {tab === "invites" ? <InvitesTab /> : <AnalyticsTab />}
    </section>
  );
}

function AdminLoginPrompt(): ReactElement {
  const { setAdminJwt } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await apiClient.post<{ jwt: string; email: string }>(
        "/auth/admin/login",
        { email, password },
      );
      setAdminJwt(data.jwt);
      window.location.reload();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Неверные credentials";
      setError(detail);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="admin-login">
      <h1>Admin Login</h1>
      <p className="admin-login__subtitle">
        Доступ только для администратора IT-Navigate
      </p>

      <form onSubmit={handleSubmit} className="auth-form">
        <label>
          <span>Email</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@it-navigator.online"
          />
        </label>
        <label>
          <span>Пароль</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <p className="auth-error">{error}</p>}

        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "Входим…" : "Войти"}
        </button>
      </form>
    </section>
  );
}

function AdminLogoutButton(): ReactElement {
  const { clearAdminJwt } = useAuth();
  return (
    <button
      className="btn-secondary"
      onClick={() => {
        clearAdminJwt();
        window.location.reload();
      }}
    >
      Admin logout
    </button>
  );
}

// ---------------- Invites Tab ----------------------------

function InvitesTab(): ReactElement {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "expired" | "revoked"
  >("active");

  const { data, isLoading, error } = useQuery<InviteListResponse>({
    queryKey: ["admin-invites", statusFilter],
    queryFn: async () => {
      const { data } = await apiClient.get<InviteListResponse>(
        `/api/admin/invites?status=${statusFilter}`,
      );
      return data;
    },
  });

  return (
    <div>
      <InviteCreateForm onCreated={() => qc.invalidateQueries({ queryKey: ["admin-invites"] })} />

      <div className="admin__invites-controls">
        <label>
          Фильтр:
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as typeof statusFilter)
            }
          >
            <option value="all">Все</option>
            <option value="active">Активные</option>
            <option value="expired">Истёкшие</option>
            <option value="revoked">Отозванные</option>
          </select>
        </label>
      </div>

      {isLoading && <p>Загрузка…</p>}
      {error && <p className="auth-error">Ошибка загрузки (проверьте admin JWT)</p>}

      {data && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Email</th>
              <th>Subset</th>
              <th>Создан</th>
              <th>Истекает</th>
              <th>URL</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((inv) => (
              <InviteRow
                key={inv.token}
                invite={inv}
                onRevoked={() =>
                  qc.invalidateQueries({ queryKey: ["admin-invites"] })
                }
              />
            ))}
          </tbody>
        </table>
      )}
      {data && data.total === 0 && <p className="admin__empty">Пусто</p>}
    </div>
  );
}

function InviteCreateForm({ onCreated }: { onCreated: () => void }): ReactElement {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [ttlHours, setTtlHours] = useState(72);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{
    name: string;
    url: string;
  } | null>(null);

  // Загружаем catalog для checkbox-grid
  const catalogQuery = useQuery<CatalogListResponse>({
    queryKey: ["catalog-admin"],
    queryFn: async () => {
      const { data } = await apiClient.get<CatalogListResponse>("/api/catalog");
      return data;
    },
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    setSuccessInfo(null);
    try {
      // null = все продукты; иначе — выбранный subset
      const catalog_subset = selectedSlugs.length > 0 ? selectedSlugs : null;
      const { data } = await apiClient.post<InviteResponse>(
        "/api/admin/invites",
        {
          client_name: clientName,
          client_email: clientEmail || null,
          catalog_subset,
          ttl_hours: ttlHours,
        },
      );

      setSuccessInfo({ name: data.client_name ?? "клиент", url: data.url });

      // Reset form
      setClientName("");
      setClientEmail("");
      setSelectedSlugs([]);
      setTtlHours(72);
      onCreated();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка создания invite";
      setError(detail);
    } finally {
      setCreating(false);
    }
  }

  return (
    <form onSubmit={submit} className="admin-form admin-form--wide">
      <h2>Создать invite</h2>

      <div className="admin-form__row">
        <label>
          <span>Имя клиента *</span>
          <input
            type="text"
            required
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="ООО Ромашка / Иван Петров"
          />
        </label>
        <label>
          <span>Email клиента</span>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="ceo@romashka.ru"
          />
        </label>
        <label className="admin-form__ttl">
          <span>TTL (часов)</span>
          <input
            type="number"
            min={1}
            max={720}
            value={ttlHours}
            onChange={(e) => setTtlHours(Number(e.target.value))}
          />
        </label>
      </div>

      {/* Click-based catalog picker */}
      {catalogQuery.isLoading && <p className="admin-form__loading">Загрузка каталога…</p>}
      {catalogQuery.data && (
        <InviteCatalogPicker
          catalog={catalogQuery.data.items}
          selected={selectedSlugs}
          onChange={setSelectedSlugs}
        />
      )}

      {error && <p className="auth-error">{error}</p>}

      {successInfo && (
        <div className="admin-form__success">
          <strong>✓ Invite создан для «{successInfo.name}»</strong>
          <p>Скопируйте URL и отправьте клиенту:</p>
          <div className="admin-form__success-url">
            <code>{successInfo.url}</code>
            <button
              type="button"
              className="btn-secondary btn-small"
              onClick={() => {
                navigator.clipboard.writeText(successInfo.url);
                alert("URL скопирован в буфер");
              }}
            >
              Копировать
            </button>
          </div>
          <button
            type="button"
            className="btn-secondary btn-small"
            onClick={() => setSuccessInfo(null)}
          >
            Скрыть
          </button>
        </div>
      )}

      <button
        type="submit"
        className="btn-primary admin-form__submit"
        disabled={creating}
      >
        {creating ? "Создаём…" : "Создать invite"}
      </button>
    </form>
  );
}

function InviteRow({
  invite,
  onRevoked,
}: {
  invite: InviteResponse;
  onRevoked: () => void;
}): ReactElement {
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    await navigator.clipboard.writeText(invite.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function revoke() {
    if (!confirm(`Отозвать invite для ${invite.client_name}?`)) return;
    try {
      await apiClient.delete(`/api/admin/invites/${invite.token}`);
      onRevoked();
    } catch {
      alert("Не удалось отозвать");
    }
  }

  return (
    <tr>
      <td>{invite.client_name ?? "—"}</td>
      <td>{invite.client_email ?? "—"}</td>
      <td>
        {invite.catalog_subset
          ? invite.catalog_subset.slice(0, 2).join(", ") +
            (invite.catalog_subset.length > 2
              ? ` +${invite.catalog_subset.length - 2}`
              : "")
          : "все"}
      </td>
      <td className="cell-date">
        {new Date(invite.created_at).toLocaleString("ru-RU")}
      </td>
      <td className="cell-date">
        {new Date(invite.expires_at).toLocaleString("ru-RU")}
      </td>
      <td>
        <button className="btn-secondary btn-small" onClick={copyUrl}>
          {copied ? "Скопировано!" : "Копировать URL"}
        </button>
      </td>
      <td>
        {invite.is_active && (
          <button className="btn-danger btn-small" onClick={revoke}>
            Revoke
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------- Analytics Tab --------------------------

function AnalyticsTab(): ReactElement {
  const sessionsQ = useQuery<SessionListResponse>({
    queryKey: ["admin-sessions"],
    queryFn: async () => {
      const { data } = await apiClient.get<SessionListResponse>(
        "/api/admin/analytics/sessions",
      );
      return data;
    },
  });

  const demosQ = useQuery<DemoUsageResponse>({
    queryKey: ["admin-demos"],
    queryFn: async () => {
      const { data } = await apiClient.get<DemoUsageResponse>(
        "/api/admin/analytics/demos",
      );
      return data;
    },
  });

  return (
    <div className="admin-analytics">
      <section>
        <h2>Demos Usage</h2>
        {demosQ.isLoading && <p>Загрузка…</p>}
        {demosQ.data && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Продукт</th>
                <th>Launches</th>
                <th>Avg duration (сек)</th>
              </tr>
            </thead>
            <tbody>
              {demosQ.data.items.map((row) => (
                <tr key={row.product_slug}>
                  <td>{row.product_slug}</td>
                  <td>{row.launch_count}</td>
                  <td>{row.avg_duration_sec?.toFixed(1) ?? "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <strong>Всего</strong>
                </td>
                <td colSpan={2}>
                  <strong>{demosQ.data.total_launches}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </section>

      <section>
        <h2>Последние сессии</h2>
        {sessionsQ.isLoading && <p>Загрузка…</p>}
        {sessionsQ.data && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Identity</th>
                <th>Mode</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Demos</th>
              </tr>
            </thead>
            <tbody>
              {sessionsQ.data.items.map((s) => (
                <tr key={s.session_id}>
                  <td>{s.identity}</td>
                  <td>{s.auth_mode}</td>
                  <td className="cell-date">
                    {new Date(s.started_at).toLocaleString("ru-RU")}
                  </td>
                  <td>{s.duration_sec ? `${s.duration_sec}s` : "—"}</td>
                  <td>{s.demo_launches_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
